import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Temporadas.css';
import { getClubes, getClubJugadores, guardarClubJugadores } from '../../services/clubesService';
import ModalEditarJugador from './ModalEditarJugador';
import { getTemporadasReal } from '../../services/temporadasService';
import { buscarJugadoresGlobal, crearJugadorReal } from '../../services/jugadoresService';
import {
  anadirJugador, aPayload, bajasConEquipos, calcularCambios, estadoFila, hayCambios,
  inscritosSinClub, moverJugador, puedeAnadir, puedeModificar, puedeReplicar, quitarJugador,
  replicarAnterior, temporadaAnterior,
} from './clubesBorrador';

const TITULO_BLOQUEO = {
  INICIADA: 'La temporada ya está iniciada: solo se pueden añadir jugadores, no cambiarlos de club ni quitarlos.',
  FINALIZADA: 'La temporada está finalizada.',
};

const aFila = (f) => ({
  jugadorId: Number(f.jugador_id), nombre: f.nombre, alias: f.alias ?? null, genero: f.genero,
  clubId: Number(f.club_id), participantes: f.participantes ?? [],
});

// Vista "Por club": plantilla de cada club en la temporada (jugador_club).
// Todo se edita en un borrador local y no se graba hasta pulsar Confirmar.
// Quitar a alguien de un club no lo da de baja de sus equipos.
export default function ClubesTab({ temporada, estado, participantes, onCambiosPendientes, onGuardado }) {
  const [clubes, setClubes] = useState([]);
  const [guardados, setGuardados] = useState([]);
  const [borrador, setBorrador] = useState([]);
  const [open, setOpen] = useState({});
  const [modalClub, setModalClub] = useState(null);
  const [editando, setEditando] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [errorCarga, setErrorCarga] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    setErrorCarga(false);
    try {
      const [cl, filas] = await Promise.all([getClubes(), getClubJugadores(temporada)]);
      const base = filas.map(aFila);
      setClubes(cl);
      setGuardados(base);
      setBorrador(base);
    } catch (err) {
      setError(err.message || 'Error al cargar los clubes.');
      setErrorCarga(true);
    } finally {
      setCargando(false);
    }
  }, [temporada]);

  useEffect(() => { cargar(); }, [cargar]);

  const dirty = useMemo(() => hayCambios(guardados, borrador), [guardados, borrador]);

  useEffect(() => { onCambiosPendientes?.(dirty); }, [dirty, onCambiosPendientes]);

  useEffect(() => {
    if (!dirty) return undefined;
    const aviso = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [dirty]);

  const nombreClub = (id) => clubes.find((c) => c.id === id)?.nombre ?? '—';
  const sinClub = useMemo(() => inscritosSinClub(participantes ?? [], borrador), [participantes, borrador]);
  const cambios = useMemo(() => calcularCambios(guardados, borrador), [guardados, borrador]);
  const total = cambios.altas.length + cambios.cambios.length + cambios.bajas.length;
  const soloLectura = estado === 'FINALIZADA';

  const asignar = (jugador, clubId) => setBorrador((b) => anadirJugador(b, jugador, clubId, estado));
  const mover = (jugadorId, clubId) => setBorrador((b) => moverJugador(b, jugadorId, clubId, estado, guardados));
  const quitar = (jugadorId) => setBorrador((b) => quitarJugador(b, jugadorId, estado, guardados));

  const replicar = async () => {
    setError(null);
    try {
      const anterior = temporadaAnterior(await getTemporadasReal(), temporada);
      if (!anterior) { setError('No hay una temporada anterior de la que copiar.'); return; }
      const filas = (await getClubJugadores(anterior)).map(aFila);
      if (!filas.length) { setError(`La temporada ${anterior} no tiene jugadores con club.`); return; }
      setBorrador((b) => replicarAnterior(b, filas, estado));
    } catch (err) {
      setError(err.message || 'Error al copiar la temporada anterior.');
    }
  };

  const confirmar = async () => {
    const avisos = bajasConEquipos(guardados, borrador);
    if (avisos.length && !window.confirm(
      `Vas a quitar del club a ${avisos.map((f) => f.nombre).join(', ')}, que siguen inscritos en equipos de esta temporada `
      + '(seguirán en sus equipos). ¿Continuar?',
    )) return;
    setGuardando(true);
    setError(null);
    try {
      await guardarClubJugadores(temporada, aPayload(borrador));
      await cargar();
      onGuardado?.();
    } catch (err) {
      setError(err.message || 'Error al guardar los clubes.');
    } finally {
      setGuardando(false);
    }
  };

  const descartar = () => setBorrador(guardados);

  // Nombre y alias se guardan al momento en el catálogo (no son del borrador):
  // se actualizan las filas ya cargadas sin perder lo que se esté editando.
  const jugadorEditado = ({ id, nombre, alias }) => {
    const aplicar = (filas) => filas.map((f) => (f.jugadorId === Number(id) ? { ...f, nombre, alias } : f));
    setGuardados(aplicar);
    setBorrador(aplicar);
  };

  const marca = (fila) => {
    const e = estadoFila(guardados, fila);
    if (e.tipo === 'nuevo') return <span className="badge-cambio nuevo">NUEVO</span>;
    if (e.tipo === 'movido') return <span className="badge-cambio movido">desde {nombreClub(e.clubAnteriorId)}</span>;
    return null;
  };

  if (cargando) return <p>Cargando clubes...</p>;

  // Sin datos fiables no se pinta nada más: con la lista vacía, todos los
  // inscritos saldrían como "sin club" y se podría confirmar algo erróneo.
  if (errorCarga) {
    return (
      <div>
        <p className="error-msg">{error}</p>
        <button className="btn btn-secondary" onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  return (
    <div>
      {error && <p className="error-msg">{error}</p>}

      <div className="clubes-barra">
        <span className={`clubes-pendientes${dirty ? ' activo' : ''}`}>
          {dirty ? `${total} cambio${total === 1 ? '' : 's'} sin confirmar` : 'Sin cambios pendientes'}
        </span>
        {puedeReplicar(estado) && (
          <button className="btn btn-secondary" onClick={replicar} disabled={guardando}>
            ⧉ Replicar temporada anterior
          </button>
        )}
        <button className="btn btn-secondary" onClick={descartar} disabled={!dirty || guardando}>Descartar</button>
        <button className="btn btn-primary" onClick={confirmar} disabled={!dirty || guardando || soloLectura}>
          {guardando ? 'Guardando…' : 'Confirmar'}
        </button>
      </div>

      {sinClub.length > 0 && (
        <div className="modalidad-section">
          <div className="modalidad-header"><h4>Inscritos sin club</h4><span className="count">{sinClub.length}</span></div>
          <div className="modalidad-body">
            {sinClub.map((j) => (
              <div key={j.jugadorId} className="equipo-card" style={{ cursor: 'default' }}>
                <div className="equipo-info"><span className="equipo-nombre">{j.nombre}</span></div>
                {puedeAnadir(estado) && (
                  <select
                    aria-label={`Asignar club a ${j.nombre}`}
                    value=""
                    onChange={(e) => e.target.value && asignar(j, Number(e.target.value))}
                  >
                    <option value="">Asignar club…</option>
                    {clubes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {clubes.map((club) => {
        const filas = borrador.filter((f) => f.clubId === club.id)
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
        const abierto = open[club.id] !== false;
        return (
          <div key={club.id} className="modalidad-section">
            <div className="modalidad-header" onClick={() => setOpen((o) => ({ ...o, [club.id]: !abierto }))}>
              <h4>{club.nombre}</h4>
              <span className="count">{filas.length} jugadores {abierto ? '▲' : '▼'}</span>
            </div>
            {abierto && (
              <div className="modalidad-body">
                {filas.map((f) => {
                  const editable = puedeModificar(estado, guardados, f.jugadorId);
                  const titulo = editable ? undefined : TITULO_BLOQUEO[estado];
                  return (
                    <div key={f.jugadorId} className="equipo-card" style={{ cursor: 'default' }}>
                      <div className="equipo-info">
                        <span className="equipo-nombre">{f.nombre}</span>
                        {f.alias && <span className="badge-alias" title="Alias: se ve en los partidos">«{f.alias}»</span>}
                        <span className="equipo-orden" style={{ marginLeft: 6 }}>{f.genero}</span>
                        {marca(f)}
                      </div>
                      <div className="equipo-actions">
                        <button
                          aria-label={`Editar nombre y alias de ${f.nombre}`}
                          title="Editar nombre y alias"
                          onClick={() => setEditando({ id: f.jugadorId, nombre: f.nombre, alias: f.alias })}
                        >✏️</button>
                        <select
                          aria-label={`Club de ${f.nombre}`}
                          value={f.clubId}
                          disabled={!editable}
                          title={titulo}
                          onChange={(e) => mover(f.jugadorId, Number(e.target.value))}
                        >
                          {clubes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                        <button
                          className="danger"
                          aria-label={`Quitar a ${f.nombre} del club`}
                          disabled={!editable}
                          title={titulo}
                          onClick={() => quitar(f.jugadorId)}
                        >✕</button>
                      </div>
                    </div>
                  );
                })}
                {!filas.length && <p className="rr-empty">Sin jugadores en este club.</p>}
                {puedeAnadir(estado) && (
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: '.5rem', fontSize: '.82rem' }}
                    onClick={() => setModalClub(club)}
                  >+ Añadir jugador</button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {editando && (
        <ModalEditarJugador jugador={editando} onGuardado={jugadorEditado} onCerrar={() => setEditando(null)} />
      )}

      {modalClub && (
        <ModalAnadirJugador
          club={modalClub}
          borrador={borrador}
          onAnadir={(j) => { asignar(j, modalClub.id); setModalClub(null); }}
          onCerrar={() => setModalClub(null)}
        />
      )}
    </div>
  );
}

// Busca en el catálogo global (o da de alta un jugador nuevo) y lo añade al
// borrador del club; no escribe la relación jugador-club, eso lo hace Confirmar.
function ModalAnadirJugador({ club, borrador, onAnadir, onCerrar }) {
  const [catalogo, setCatalogo] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoGenero, setNuevoGenero] = useState('F');
  const [error, setError] = useState(null);

  useEffect(() => {
    buscarJugadoresGlobal()
      .then((l) => setCatalogo([...l].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))))
      .catch((err) => setError(err.message || 'Error al cargar jugadores.'));
  }, []);

  const presentes = new Set(borrador.map((f) => f.jugadorId));
  const candidatos = catalogo
    .filter((j) => !presentes.has(Number(j.id)))
    .filter((j) => j.nombre.toLowerCase().includes(filtro.trim().toLowerCase()))
    .slice(0, 50);

  const elegir = (j) => onAnadir({ jugadorId: Number(j.id), nombre: j.nombre, genero: j.genero });

  const crear = async () => {
    if (!nuevoNombre.trim()) return;
    try {
      elegir(await crearJugadorReal({ nombre: nuevoNombre.trim(), genero: nuevoGenero }));
    } catch (err) {
      setError(err.message || 'Error al crear el jugador.');
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-box">
        <h3>Añadir jugador — {club.nombre}</h3>
        {error && <p className="error-msg">{error}</p>}
        <div className="form-row">
          <label htmlFor="buscar-jugador-club">Buscar en el catálogo</label>
          <input id="buscar-jugador-club" value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Nombre…" />
        </div>
        <div className="clubes-candidatos">
          {candidatos.map((j) => (
            <button key={j.id} className="btn btn-secondary" onClick={() => elegir(j)}>{j.nombre} ({j.genero})</button>
          ))}
          {!candidatos.length && <p className="rr-empty">Sin resultados.</p>}
        </div>
        <div className="new-player-form">
          <input placeholder="Nuevo jugador..." value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)} />
          <select value={nuevoGenero} onChange={(e) => setNuevoGenero(e.target.value)} aria-label="Género">
            <option value="F">F</option>
            <option value="M">M</option>
          </select>
          <button className="btn btn-secondary" onClick={crear}>+ Crear y añadir</button>
        </div>
        <div style={{ marginTop: '1rem', textAlign: 'right' }}>
          <button className="btn btn-secondary" onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
